import { Fragment, useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../core/components/atoms/Button';
import Container from '../../../core/components/atoms/Container';
import { Icon } from '../../../core/components/atoms/Icon';
import { Tag } from '../../../core/components/atoms/Tag';
import { Toast, toast } from '../../../core/components/atoms/Toast';
import PageHeader from '../../../core/components/molecules/Header';
import { Typography } from '../../../core/components/molecules/Typography';
import ContentCard from '../../../core/components/organism/ContentCard';
import env from '../../../core/env';
import { Colors } from '../../../core/models/disciplines';
import { getDisciplineName } from '../../../core/utils/new_discipline';
import { CardContentAudio } from '../components/CardContent/audio';
import { CardContentExercise } from '../components/CardContent/exercise';
import { CardContentLesson } from '../components/CardContent/lesson';
import { CardContentMaterial } from '../components/CardContent/material';
import { EmptyFavs } from '../components/EmptyFavs';
import FilterTheme from '../components/ModalFiltro';
import { useGetFavorites } from '../hooks';
import { favoriteFilterStore } from '../stores/favoriteFilterStore';
import { favoriteStore } from '../stores/favoriteStore';
import { categoryIcon, categoryURL } from '../utils/card';
import './styles.css';

export function FavoritesPage() {
  const navigate = useNavigate();

  const { mutate, isPending, isError } = useGetFavorites();
  const { discipline, category, removeCategory, removeDiscipline } =
    favoriteFilterStore();
  const { favorites } = favoriteStore();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    mutate();
  }, []);

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'error',
        message: 'Não foi possivel carregar seus favoritos',
        title: 'Erro!',
      });
    }
  }, [isError]);

  const renderContent = () => {
    if (favorites.length === 0) {
      return <EmptyFavs />;
    }

    //TODO: Depois que existir na plataforma nova, retirar
    const goToLives = (slug: string) => {
      window.location.href =
        env.REACT_APP_PLATFORM_URL + `/favoritos/lives/${slug}`;
    };

    const renderCardContent = (
      category: string,
      time: string,
      type: string,
      teacher: string,
      text: string
    ) => {
      if (category === 'Material' && type === 'audios') {
        return <CardContentAudio time={time || '--:--'} />;
      }

      if (category === 'Material') {
        return <CardContentMaterial type={type || ''} />;
      }

      if (category === 'Exercício') {
        return <CardContentExercise text={text || ''} />;
      }

      return (
        <CardContentLesson time={time || '--:--'} teacher={teacher || null} />
      );
    };

    return (
      <div className='flex flex-col gap-lg'>
        {favorites.map((it, i) => (
          <ContentCard
            key={i}
            color={(it.ancestors?.area?.slug as Colors) || 'default'}
            content={
              it.file_category && it.file_category === 'audios'
                ? 'Audio'
                : it.category
            }
            title={it.category === 'Exercício' ? undefined : it.title}
            favorite={true}
            icon={categoryIcon(
              it.file_category && it.file_category === 'audios'
                ? 'Audio'
                : it.category
            )}
            action={() =>
              it.category === 'Live'
                ? goToLives(it.slug)
                : navigate(
                    categoryURL(
                      it.ancestors,
                      it.slug,
                      it.subjects[0]?.slug,
                      it.file_category && it.file_category === 'audios'
                        ? 'Audio'
                        : it.category,
                      it
                    )
                  )
            }
            id={it._id}
            discipline={
              it.ancestors
                ? getDisciplineName(it.ancestors?.area?.slug)
                : undefined
            }
            disciplineSlug={it.ancestors ? it.ancestors?.area?.slug : undefined}
          >
            {renderCardContent(
              it.category,
              it.duration || '--:--',
              it.file_category || '',
              it.teacher,
              it.statement || ''
            )}
          </ContentCard>
        ))}
      </div>
    );
  };

  const renderLoading = () => {
    return (
      <Fragment>
        {Array.from({ length: 10 }, (_, index) => (
          <ContentLoader
            key={index}
            backgroundColor={'#a7adba'}
            foregroundColor={'#c0c5ce'}
            viewBox='0 0 500 35'
          >
            <rect x='0' y='10' rx='2' ry='2' width='300' height='13' />
            <rect x='0' y='25' rx='2' ry='2' width='250' height='10' />
          </ContentLoader>
        ))}
      </Fragment>
    );
  };

  const renderTags = () => {
    return (
      <Fragment>
        {discipline.length > 0 &&
          discipline.map((it, i) => (
            <Tag
              variant='primary'
              key={i}
              className='flex flex-row items-center gap-sm px-2'
            >
              {getDisciplineName(it)}
              <Icon
                Icon='close'
                type='material'
                className='cursor-pointer text-sm'
                onClick={() => handleRemove(it, 'discipline')}
              />
            </Tag>
          ))}
        {category.length > 0 &&
          category.map((it, i) => (
            <Tag
              variant='primary'
              key={i}
              className='flex flex-row items-center gap-sm px-2'
            >
              {getDisciplineName(it)}
              <Icon
                Icon='close'
                type='material'
                className='cursor-pointer text-sm'
                onClick={() => handleRemove(it, 'category')}
              />
            </Tag>
          ))}
      </Fragment>
    );
  };

  const onSubmit = () => {
    setIsOpen(false);
    mutate();
  };

  const handleRemove = (item: string, type: string) => {
    if (type === 'category') {
      removeCategory(item);
    } else {
      removeDiscipline(item);
    }

    mutate();
  };

  return (
    <Container gap='xxl' direction='col' className='xs:mb-[30px] xl:mb-[30px]'>
      <PageHeader title='Favoritos' />
      <div className='flex h-fit w-full flex-row items-end justify-between'>
        <Typography size='small'>
          {favorites?.length || 0} conteúdos favoritados
        </Typography>
        <Button variant='text' onClick={() => setIsOpen(true)} className='p-0'>
          <Icon
            Icon='filter_list'
            type='material'
            className='xs:text-sm md:text-md'
          />
          <span className='xs:text-sm md:text-md '>Filtrar conteúdos</span>
        </Button>
      </div>
      {(discipline.length > 0 || category.length > 0) && (
        <div id='filterTags' className='flex w-full flex-row flex-wrap gap-xs'>
          {renderTags()}
        </div>
      )}

      {isPending ? renderLoading() : renderContent()}

      <FilterTheme
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={() => onSubmit()}
      />
      {isError && <Toast />}
    </Container>
  );
}
